import React, { useState } from "react";
import { Button, Collapse, Modal } from "react-bootstrap";
import { AppConfiguration, SupportRoutes } from "src/common";
import { Icon } from "src/components";
import _ from "lodash";
import add_folder_img from "src/assets/images/quick-start-menu/add-folder.svg";
import save_recovery_file_express_img from "src/assets/images/quick-start-menu/save-recovery-file-express.svg";
import save_recovery_file_full_img from "src/assets/images/quick-start-menu/save-recovery-file.svg";
import send_email_img from "src/assets/images/quick-start-menu/send-email.svg";
import share_folder_img from "src/assets/images/quick-start-menu/share-folder.svg";
import upgrade_img from "src/assets/images/quick-start-menu/upgrade.svg";
import upload_file_img from "src/assets/images/quick-start-menu/upload-file.svg";

type QuickStartItem = {
  key: string;
  icon_class: string;
  label: string;
  description: string;
  image_url?: string;
};

const is_express = AppConfiguration.buildForExpress();

const items: QuickStartItem[] = [
  {
    key: 'mail',
    icon_class: 'icon-mail5',
    label: 'Send an Email',
    description: 'Send a secure PreVeil email by selecting the Compose button.',
    image_url: send_email_img,
  },
  {
    key: 'upload',
    icon_class: 'icon-upload',
    label: 'Upload a File',
    description: 'In Drive, click the upload menu button and browse to your file.',
    image_url: upload_file_img,
  },
  {
    key: 'add_folder',
    icon_class: 'icon-folder-plus2',
    label: 'Add a Folder',
    description: 'In Drive, click the add new folder button to add a folder to your content.',
    image_url: add_folder_img,
  },
  {
    key: 'share_folder',
    icon_class: 'icon-more2',
    label: 'Share a Folder',
    description: 'In Drive, click the more button in the right of your row to securely share a folder.',
    image_url: share_folder_img,
  },
  !is_express ? {
    key: 'add_device',
    icon_class: 'icon-mobile2',
    label: 'Get the Mobile App',
    description: 'Use your PreVeil content on a mobile device.',
  } : {
    key: 'upgrade',
    icon_class: 'icon-arrow-up16',
    label: 'Upgrade to Full PreVeil',
    description: 'Upgrade to the PreVeil Desktop application for added features like Outlook, Gmail, and File Explorer integration.',
    image_url: upgrade_img,
  },
  {
    key: 'save_recovery_file',
    icon_class: 'icon-file-check',
    label: 'Save the Recovery File',
    description: `In settings Account Recovery, select the ${is_express ? "Download Recovery File" : "Create Recovery File"} button.`,
    image_url: is_express ? save_recovery_file_express_img : save_recovery_file_full_img,
  }
];

/* Description:  A collapsible Quick Start menu that overlays the left navigation. 
  When expanded, it reveals a set of guided actions: sending an email, uploading a file, 
  adding a folder, share folder, add device, save recovery file, take tour. Each item in the menu can be individually expanded to show a description, 
  and optionally display an image. */
function QuickStartMenuComponent() {
  const [open, setOpen] = useState<boolean>(false);
  const [open_key, setOpenKey] = useState<string>();
  const [modal_item, setModalItem] = useState<{ image: string, label: string }>();

  function toggleItem(key: string) {
    setOpenKey(open_key === key ? undefined : key);
  };

  function displayMenuItems() {
    return <>
      {_.map(items, (item: QuickStartItem, i: number) => {
        return <div key={i}>
          <Button variant="quick-start" onClick={() => toggleItem(item.key)}>
            <span><Icon className={item.icon_class} />{item.label}</span> 
            <Icon className={`${open_key ? "ficon-chevron-up" : "ficon-chevron-down"}`} />
          </Button>
          {open_key === item.key && <div className="sub-menu">
            <p>{item.description}</p>
            {item.key === "add_device" ?  <a
                href={SupportRoutes.add_device}
                target="_blank"
                rel="noreferrer"
                className="btn btn-primary btn-sm mt-1"
              >
                <span><Icon className="icon-mobile"></Icon>Show me</span>
              </a> :
            <Button size="sm" onClick={() => !!item.image_url && setModalItem({ image: item.image_url, label: item.label })}>Show Me</Button>}
          </div>}
        </div>;
      })}
    </>;
  };

  return (
    <div className="quick-start-menu">
      <Button onClick={() => setOpen(!open)} variant="quick-start" className="main-button">
        PreVeil Quick Start <Icon className={`${open ? "ficon-chevron-down" : "ficon-chevron-up"}`} />
      </Button>
      <Collapse in={open}>
        <div className={`${open ? "border-top" : ""}`}>
          {open && <>
            {displayMenuItems()}
            <a
              href={SupportRoutes.take_a_tour}
              target="_blank"
              rel="noreferrer"
              className="btn btn-quick-start"
            >
              <span><Icon className="icon-question3" /> Take a Tour</span>
            </a>
          </>}
        </div>
      </Collapse>
      {!!modal_item && 
        <Modal fullscreen={true} dialogClassName="p-4" centered show={!!modal_item} onHide={() => setModalItem(undefined)}>
          <Modal.Header closeButton>
            <Modal.Title>{modal_item.label}</Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <img className="img-fluid w-100" style={{ objectFit: 'contain', maxHeight: '70vh' }} src={modal_item.image} alt={modal_item.label} />
          </Modal.Body>
        </Modal>
      }
    </div>
  );
}

export default React.memo(QuickStartMenuComponent);